import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function _ensureUrlScheme(url) {
  if (!url) return url;
  try {
    const hasScheme = /^https?:\/\//i.test(url);
    return hasScheme ? url : `https://${url}`;
  } catch (e) {
    return url;
  }
}

/**
 * Upload a single file directly to Cloudflare R2
 * @param {File} file - The file to upload
 * @param {string} token - Authentication token
 * @returns {Promise<string>} - The public URL of the uploaded file
 */
export async function uploadToR2(file, token) {
  try {
    // Step 1: Request presigned URL from backend
    const presignedResponse = await axios.post(
      `${API_URL}/r2/generate-presigned-url`,
      {
        filename: file.name,
        content_type: file.type
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const { upload_url, public_url } = presignedResponse.data;

    // Step 2: Upload file directly to R2 using presigned URL
    const formData = new FormData();
    formData.append('file', file);

    await axios.put(upload_url, file, {
      headers: {
        'Content-Type': file.type
      }
    });

    // Validate with backend (backend may normalize domain/scheme)
    try {
      const valid = await validateR2Upload([public_url], token);
      if (valid && Array.isArray(valid.validated_urls) && valid.validated_urls.length > 0) {
        return _ensureUrlScheme(valid.validated_urls[0]);
      }
    } catch (e) {
      // fallback to best-effort URL
    }

    return _ensureUrlScheme(public_url);
  } catch (error) {
    console.error('Error uploading to R2:', error);
    throw new Error(error.response?.data?.detail || 'Failed to upload file to R2');
  }
}

/**
 * Upload multiple files to Cloudflare R2 in parallel
 * @param {File[]} files - Array of files to upload
 * @param {string} token - Authentication token
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<string[]>} - Array of public URLs
 */
export async function uploadMultipleToR2(files, token, onProgress = null) {
  if (!files || files.length === 0) {
    return [];
  }

  try {
    // Step 1: Request multiple presigned URLs
    const requests = files.map(file => ({
      filename: file.name,
      content_type: file.type
    }));

    const presignedResponse = await axios.post(
      `${API_URL}/r2/generate-presigned-urls`,
      requests,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const { presigned_urls } = presignedResponse.data;

    // Step 2: Upload all files in parallel
    const uploadPromises = presigned_urls.map(async (urlData, index) => {
      const file = files.find(f => f.name === urlData.filename);
      if (!file) return null;

      try {
        // Upload using presigned PUT (R2 compatible)
        await axios.put(urlData.upload_url, file, {
          headers: {
            'Content-Type': urlData.content_type || file.type
          }
        });

        if (onProgress) {
          onProgress(index + 1, files.length);
        }

        return urlData.public_url;
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        return null;
      }
    });

    const results = await Promise.all(uploadPromises);

    // Filter out failed uploads
    const successfulUrls = results.filter(url => url !== null).map(u => _ensureUrlScheme(u));

    // Validate with backend to ensure URLs are reachable/normalized
    try {
      const validation = await validateR2Upload(successfulUrls, token);
      if (validation && Array.isArray(validation.validated_urls) && validation.validated_urls.length > 0) {
        return validation.validated_urls.map(u => _ensureUrlScheme(u));
      }
    } catch (e) {
      // If validation fails, still return best-effort URLs
    }

    return successfulUrls;
  } catch (error) {
    console.error('Error uploading multiple files to R2:', error);
    throw new Error(error.response?.data?.detail || 'Failed to upload files to R2');
  }
}

/**
 * Validate uploaded URLs with backend
 * @param {string[]} urls - Array of uploaded file URLs
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} - Validation result
 */
export async function validateR2Upload(urls, token) {
  try {
    const response = await axios.post(
      `${API_URL}/r2/validate-upload`,
      { file_urls: urls },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error validating R2 upload:', error);
    return { validated_urls: [], count: 0 };
  }
}

/**
 * Check if R2 is configured and available
 * @returns {boolean}
 */
export function isR2Available() {
  // You can add a health check endpoint if needed
  return true; // Assume R2 is available, will fail gracefully if not
}
